import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { formatBytes } from '../board/items/FileItem';
import { api } from '../sync/api';
import { logout, useAuth } from '../sync/authStore';
import {
  createBoard,
  createInvite,
  createProject,
  deleteBoard,
  deleteProject,
  listDashboard,
  updateBoard,
  updateProject,
  type BoardSummary,
  type Project,
} from '../sync/boardsApi';
import { duplicateBoard } from '../sync/duplicate';
import { useUploads } from '../sync/uploadQueue';
import { IconDots } from '../ui/icons';
import { PaneMenu } from '../ui/PaneMenu';
import { thumbnailUrl } from './base';
import { paths } from './router';
import { BUILD, checkForUpdate, formatBuilt, hardRefresh, useUpdate } from './version';

const COLLAPSED_KEY = 'vds:dashboard:collapsed';

function loadCollapsed(): Set<string> {
  try {
    const raw = localStorage.getItem(COLLAPSED_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

function saveCollapsed(ids: Set<string>): void {
  try {
    localStorage.setItem(COLLAPSED_KEY, JSON.stringify([...ids]));
  } catch {
    /* private mode; folds just won't be remembered */
  }
}

interface Usage {
  total: number;
  boards: Record<string, number>;
}

/** Board list: folders (owned boards), the top level, and boards shared by others; cards with thumbnails. */
export function Dashboard() {
  const user = useAuth((s) => s.user);
  const [boards, setBoards] = useState<BoardSummary[] | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Set<string>>(loadCollapsed);
  /** Boards with an operation in flight, with a label for the card. */
  const [busy, setBusy] = useState<Record<string, string>>({});
  const [menu, setMenu] = useState<{ id: string; anchor: HTMLElement } | null>(null);
  const pendingUploads = useUploads((s) => s.pending);
  const [usage, setUsage] = useState<Usage | null>(null);
  const update = useUpdate((s) => s);

  const refresh = useCallback(async () => {
    try {
      const d = await listDashboard();
      setBoards(d.boards);
      setProjects(d.projects);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  const loadUsage = useCallback(() => {
    api
      .get<{ total: number; boards: { id: string; bytes: number }[] }>('/usage')
      .then((u) => setUsage({ total: u.total, boards: Object.fromEntries(u.boards.map((b) => [b.id, b.bytes])) }))
      .catch(() => setUsage(null));
  }, []);

  useEffect(() => {
    void refresh();
    loadUsage();
  }, [refresh, loadUsage]);

  /** Runs an operation, shows its failure, and refreshes the list afterwards. */
  const run = async (boardId: string | null, label: string, fn: () => Promise<unknown>): Promise<void> => {
    if (boardId) setBusy((b) => ({ ...b, [boardId]: label }));
    try {
      await fn();
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      if (boardId) {
        setBusy((b) => {
          const next = { ...b };
          delete next[boardId];
          return next;
        });
      }
      await refresh();
    }
  };

  const onCreate = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const b = await createBoard(title.trim() || 'Untitled board');
      setTitle('');
      window.location.hash = paths.board(b.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const rename = (b: BoardSummary) => {
    const next = prompt('Board title', b.title);
    if (next === null || next.trim() === b.title) return;
    void run(b.id, 'Renaming…', () => updateBoard(b.id, { title: next }));
  };

  const archive = (b: BoardSummary, archived: boolean) =>
    void run(b.id, archived ? 'Archiving…' : 'Restoring…', () => updateBoard(b.id, { archived }));

  const remove = (b: BoardSummary) => {
    if (!confirm(`Delete "${b.title}" and all its images? This cannot be undone.`)) return;
    void run(b.id, 'Deleting…', () => deleteBoard(b.id));
  };

  const duplicate = (b: BoardSummary) =>
    void run(b.id, 'Duplicating…', async () => {
      await duplicateBoard(b.id, `Copy of ${b.title}`);
      loadUsage();
    });

  const moveTo = (b: BoardSummary, projectId: string | null) =>
    void run(b.id, 'Moving…', () => updateBoard(b.id, { projectId }));

  const newFolder = () => {
    const name = prompt('Folder name', '');
    if (name === null || !name.trim()) return;
    void run(null, '', () => createProject(name));
  };

  const renameFolder = (p: Project) => {
    const next = prompt('Folder name', p.name);
    if (next === null || !next.trim() || next.trim() === p.name) return;
    void run(null, '', () => updateProject(p.id, { name: next }));
  };

  const removeFolder = (p: Project) => {
    if (!confirm(`Delete the folder "${p.name}"? Its boards stay and move to the top level.`)) return;
    void run(null, '', () => deleteProject(p.id));
  };

  const toggleFold = (id: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      saveCollapsed(next);
      return next;
    });
  };

  const invite = async () => {
    const r = await createInvite();
    const url = `${window.location.origin}${window.location.pathname}${paths.register(r.code)}`;
    setInviteUrl(url);
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      /* clipboard may be unavailable; the link is shown anyway */
    }
  };

  const visible = boards?.filter((b) => showArchived || !b.archived) ?? [];
  const shared = visible.filter((b) => b.role !== 'owner');
  const byProject = new Map<string, BoardSummary[]>();
  const topLevel: BoardSummary[] = [];
  for (const b of visible) {
    if (b.role !== 'owner') continue;
    if (b.projectId && projects.some((p) => p.id === b.projectId)) {
      const list = byProject.get(b.projectId) ?? [];
      list.push(b);
      byProject.set(b.projectId, list);
    } else {
      topLevel.push(b);
    }
  }
  const grouped = projects.length > 0 || shared.length > 0;
  const menuBoard = menu ? (boards?.find((b) => b.id === menu.id) ?? null) : null;
  const closeMenu = useCallback(() => setMenu(null), []);
  const menuAction = (fn: () => void) => () => {
    setMenu(null);
    fn();
  };

  const grid = (list: BoardSummary[]) => (
    <ul className="board-grid">
      {list.map((b) => (
        <BoardCard
          key={b.id}
          board={b}
          bytes={usage?.boards[b.id] ?? 0}
          busyLabel={busy[b.id]}
          onMenu={(anchor) => setMenu((m) => (m?.id === b.id ? null : { id: b.id, anchor }))}
        />
      ))}
    </ul>
  );

  return (
    <main className="page" onScroll={closeMenu}>
      <header className="page-header">
        <h1 title="Visual Data Slate">VDS</h1>
        <div className="page-header-right">
          {pendingUploads > 0 && <span className="tag">{pendingUploads} uploads pending</span>}
          {usage && <span className="muted" title="Storage used by your images and attachments">{formatBytes(usage.total)}</span>}
          <span className="muted">{user?.name}</span>
          <button type="button" className="btn" onClick={invite}>
            Invite
          </button>
          <button type="button" className="btn" onClick={() => void logout()}>
            Sign out
          </button>
        </div>
      </header>

      {inviteUrl && (
        <div className="notice">
          <span>Invite link (valid 7 days, one use), copied to clipboard:</span>
          <code>{inviteUrl}</code>
          <button type="button" className="btn" onClick={() => setInviteUrl(null)}>
            Dismiss
          </button>
        </div>
      )}

      <section className="win">
        <div className="win-title">
          <span className="win-title-text">Boards</span>
          <label className="win-title-opt">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
            archived
          </label>
        </div>
        <div className="win-body">
          <form className="row-form" onSubmit={onCreate}>
            <input placeholder="New board title" value={title} onChange={(e) => setTitle(e.target.value)} />
            <button type="submit" className="btn btn-primary">
              New board
            </button>
            <button type="button" className="btn" onClick={newFolder} title="Folders group your own boards on this page">
              New folder
            </button>
          </form>

          {error && <div className="form-error">{error}</div>}
          {boards === null && !error && <p className="muted">Loading…</p>}
          {boards !== null && visible.length === 0 && projects.length === 0 && <p className="muted">No boards yet. Create one above.</p>}

          {projects.map((p) => {
            const list = byProject.get(p.id) ?? [];
            return (
              <Section
                key={p.id}
                title={p.name}
                count={list.length}
                collapsed={collapsed.has(p.id)}
                onToggle={() => toggleFold(p.id)}
                actions={
                  <>
                    <button type="button" className="btn btn-small" onClick={() => renameFolder(p)}>
                      Rename
                    </button>
                    <button type="button" className="btn btn-small danger" onClick={() => removeFolder(p)}>
                      Delete
                    </button>
                  </>
                }
              >
                {list.length ? grid(list) : <p className="muted dash-empty">Empty. Use a board's menu to move it here.</p>}
              </Section>
            );
          })}

          {grouped ? (
            topLevel.length > 0 && (
              <Section title="Boards" count={topLevel.length} collapsed={collapsed.has('top')} onToggle={() => toggleFold('top')}>
                {grid(topLevel)}
              </Section>
            )
          ) : (
            grid(topLevel)
          )}

          {shared.length > 0 && (
            <Section title="Shared with me" count={shared.length} collapsed={collapsed.has('shared')} onToggle={() => toggleFold('shared')}>
              {grid(shared)}
            </Section>
          )}
        </div>
      </section>

      {menu && menuBoard && (
        <PaneMenu anchor={menu.anchor} placement="below" onRequestClose={closeMenu} className="board-menu">
          <button type="button" onClick={menuAction(() => (window.location.hash = paths.board(menuBoard.id)))}>
            Open
          </button>
          <button type="button" onClick={menuAction(() => rename(menuBoard))}>
            Rename…
          </button>
          <button type="button" onClick={menuAction(() => duplicate(menuBoard))}>
            Duplicate
          </button>
          {menuBoard.role === 'owner' && projects.length > 0 && (
            <>
              <div className="sep" />
              <div className="menu-heading">Move to</div>
              {menuBoard.projectId && (
                <button type="button" onClick={menuAction(() => moveTo(menuBoard, null))}>
                  Top level
                </button>
              )}
              {projects
                .filter((p) => p.id !== menuBoard.projectId)
                .map((p) => (
                  <button key={p.id} type="button" onClick={menuAction(() => moveTo(menuBoard, p.id))}>
                    {p.name}
                  </button>
                ))}
            </>
          )}
          <div className="sep" />
          <button type="button" onClick={menuAction(() => archive(menuBoard, !menuBoard.archived))}>
            {menuBoard.archived ? 'Unarchive' : 'Archive'}
          </button>
          {menuBoard.role === 'owner' && (
            <button type="button" className="danger" onClick={menuAction(() => remove(menuBoard))}>
              Delete…
            </button>
          )}
        </PaneMenu>
      )}

      <p className="muted page-foot">
        <a href={paths.local()}>Local scratch board</a> · stays in this browser, never uploaded
      </p>
      <p className="muted page-foot page-foot-version">
        <span title={`commit ${BUILD.commit}`}>
          VDS {BUILD.version} · build {formatBuilt(BUILD.built)}
        </span>
        {update.available ? (
          <>
            <span className="good">new version available (built {formatBuilt(update.available.built)})</span>
            <button type="button" className="btn btn-primary" onClick={() => void hardRefresh()}>
              Reload to update
            </button>
          </>
        ) : (
          <>
            <span>{update.checking ? 'checking…' : update.error ? `check failed: ${update.error}` : update.checkedAt ? 'up to date' : ''}</span>
            <button type="button" className="btn" disabled={update.checking || !import.meta.env.PROD} onClick={() => void checkForUpdate()}>
              Check for updates
            </button>
          </>
        )}
        <button type="button" className="btn" title="Drops the cached app shell and reloads from the server" onClick={() => void hardRefresh()}>
          Hard refresh
        </button>
      </p>
    </main>
  );
}

function Section({
  title,
  count,
  collapsed,
  onToggle,
  actions,
  children,
}: {
  title: string;
  count: number;
  collapsed: boolean;
  onToggle: () => void;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="dash-section">
      <header className="dash-section-head">
        <button type="button" className="dash-fold" onClick={onToggle} aria-expanded={!collapsed} title={collapsed ? 'Expand' : 'Collapse'}>
          {collapsed ? '+' : '−'}
        </button>
        <h2>{title}</h2>
        <span className="muted dash-count">{count}</span>
        {actions && <span className="dash-section-actions">{actions}</span>}
      </header>
      {!collapsed && children}
    </section>
  );
}

function BoardCard({
  board: b,
  bytes,
  busyLabel,
  onMenu,
}: {
  board: BoardSummary;
  bytes: number;
  busyLabel: string | undefined;
  onMenu: (anchor: HTMLElement) => void;
}) {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const thumb = b.thumbnailAt ? thumbnailUrl(b.id, b.thumbnailAt) : null;
  const showThumb = thumb !== null && thumb !== failedUrl;
  return (
    <li className={`board-card${b.archived ? ' is-archived' : ''}${busyLabel ? ' is-busy' : ''}`}>
      <a className="board-card-thumb" href={paths.board(b.id)} aria-label={`Open ${b.title}`}>
        {showThumb ? (
          <img src={thumb} alt="" loading="lazy" decoding="async" onError={() => setFailedUrl(thumb)} />
        ) : (
          <span className="board-card-empty">no preview yet</span>
        )}
        {busyLabel && <span className="board-card-busy">{busyLabel}</span>}
      </a>
      <div className="board-card-body">
        <a className="board-card-title" href={paths.board(b.id)} title={b.title}>
          {b.title}
        </a>
        <span className="muted board-card-meta">
          {b.role !== 'owner' ? `${b.role} · ` : ''}
          {b.assetCount ?? 0} files{bytes ? ` (${formatBytes(bytes)})` : ''} · {formatDate(b.updatedAt)}
        </span>
        <button
          type="button"
          className="btn btn-icon board-card-menu"
          title="Board menu"
          aria-label={`Menu for ${b.title}`}
          aria-haspopup="menu"
          onClick={(e) => onMenu(e.currentTarget)}
        >
          <IconDots />
        </button>
      </div>
    </li>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}
